import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';
import { getStripeInfo } from '../../../../../selectors/earnings';
import StripePaymentTerms from '../../_partials/StripePaymentTerms';
import WithdrawalErrorBlock from '../../_partials/WithdrawalErrorBlock';

import s from './StripeWithdrawIncompleteConnectionModal.scss';

export interface Props {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  requestClose: () => void;
  /** Function called when onboarding is initiated */
  onSuccess?: () => void;
  /** Modal class name */
  className?: string;
}

const StripeWithdrawIncompleteConnectionModal: FC<Props> = ({
  isOpen,
  requestClose,
  onSuccess,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const stripeInfo = useSelector(getStripeInfo);
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.STRIPE_CONNECT)
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleFinishOnboarding = () => {
    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.Stripe,
        process_step: 'setup',
      })
    );

    // Use the country from stripe info if available
    const country = stripeInfo?.country || 'usa';

    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.STRIPE_CONNECT,
        method: SimpleXHRMethod.POST,
        data: {
          country,
        },
        onSuccess: (response: any) => {
          const onboardingUrl = response?.onboardingUrl;
          if (onboardingUrl) {
            window.location.href = onboardingUrl;
          } else {
            setErrorMessage(
              t('studio.earnings.stripe.setup.modal.error.noUrl')
            );
          }
        },
        onError: (response: any) => {
          setErrorMessage(
            response?.errorDescription ||
              t('studio.earnings.stripe.setup.modal.error.message')
          );
        },
      })
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={requestClose}
      className={className}
      contentClassName={s['content']}
      heading={t('studio.earnings.stripe.setup.modal.title')}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          key="cancel"
          size="large"
          variant="main"
          onClick={requestClose}
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          key="finish"
          size="large"
          variant="brand-green"
          onClick={handleFinishOnboarding}
          disabled={isLoading}
          loading={isLoading}
        >
          {t(
            'studio.earnings.stripe.incompleteConnection.modal.buttons.finishOnboarding'
          )}
        </BrandedButton>,
      ]}
    >
      <WithdrawalErrorBlock message={errorMessage} />

      <div className={s['inner-content']}>
        <div className={s['description']}>
          {t('studio.earnings.stripe.incompleteConnection.modal.description')}
        </div>

        <StripePaymentTerms className={s['terms']} />
      </div>
    </Modal>
  );
};

StripeWithdrawIncompleteConnectionModal.displayName =
  'StripeWithdrawIncompleteConnectionModal';

export default StripeWithdrawIncompleteConnectionModal;
